/* Built In Imports */
import NextLink from 'next/link';
import { useContext, useEffect, useState } from 'react';

/* External Imports */
import { Box, Link } from '@chakra-ui/react';
import Masonry from 'react-masonry-css';

/* Internal Imports */
/* Components */
import MediaArticleCard from '@components/Card/MediaArticleCard';
import QuoteCard from '@components/Card/QuoteCard';
import TopicBanner from '@components/HeaderBanner/TopicBanner';
import Breadcrum from '@components/Navigation/Breadcrum';
import HeadComponent from '@components/Utility/HeadComponent';
import Loader from '@components/Utility/Loader';
import { getLocale } from '@components/Utility/Shared/SharedService';
import config from '@config';
import { AuthContext } from '@store/auth-context';

/* Services */
import {
  fetchFooterData,
  fetchHeaderMenuData,
  fetchHeaderTopMenuData,
} from 'services/commonService';
import { fetchTopicData, getWisdomLandingPaths } from 'services/wisdomService';

/**
 * Renders the Landing Page for selected Topic
 *
 * @function
 * @param {string} region - Current User Region
 * @param {string} language - Current User Language
 * @param {string} pageData - Selected Topic Page Data
 * @returns {ReactElement} Selected Topic Landing Page
 */
export default function SelectedTopicLandingPage({
  region,
  language,
  pageData,
  page,
}) {
  // console.log('pageDatavpageData', pageData);
  const authContext = useContext(AuthContext);
  // const [currentUserRegion, setCurrentUserRegion] = useState(region);
  const [posts, setPosts] = useState(pageData.posts);
  const [cards, setCards] = useState(posts?.cards);
  const [isLoader, setIsLoader] = useState(false);

  const breakpointColumnsObj = {
    default: 3,
    1100: 3,
    980: 2,
    768: 2,
    736: 1,
  };

  useEffect(() => {
    // const currentRegion = config.locales.find(
    //   locale => locale.region_sf === region
    // ).region;
    authContext.RegionAndLang(region, language);
    // setCurrentUserRegion(currentRegion);
  }, [cards, region]);

  // Topic link is not properly mapped in API.
  const breadcrumbs = pageData?.breadcrumbs?.map(breadcrumb =>
    breadcrumb.link === `/${language}/topic`
      ? {
        title: breadcrumb.title,
        link: `/${language}/wisdom/topics`,
      }
      : breadcrumb
  );

  return pageData && pageData.posts && pageData.posts.cards ? (
    <Box pos="relative" float="left" w="100%">
      {pageData._seoMetaTags && pageData.availableLocales ? (
        <HeadComponent
          data={pageData._seoMetaTags}
          availableLocals={pageData.availableLocales}
        >
          {' '}
        </HeadComponent>
      ) : null}

      <Box>
        <Breadcrum
          page={breadcrumbs}
          alsoin={[]}
          url={pageData.url}
          region={region}
          lang={language}
        />
      </Box>

      {/* wisdom banner section */}
      <Box
        marginTop={{ base: '12px', sm: '12px', md: '0', lg: '0' }}
        pos="relative"
        float="left"
        w="100%"
      >
        <TopicBanner title={pageData.title} size={'small'} lang={language} />
      </Box>

      {/* view content section */}
      <Box pos="relative" float="left" w="100%" textAlign="center" mb="1vw">
        <Box maxW="1250px" m="0 auto" p="0px 20px">
          {/* <ResponsiveMasonry
            columnsCountBreakPoints={{ 350: 1, 750: 3, 1100: 3 }}
          > */}
          <Masonry breakpointCols={breakpointColumnsObj} gutter="5px">
            {cards?.map((post, inde) => {
              if (post.contentType === 'quote') {
                return (
                  <QuoteCard key={post.uuid} {...post} lang={language} />
                );
              }
              if (post.contentType) {
                return <MediaArticleCard {...post} key={inde} />;
              }
            })}
          </Masonry>
          {/* </ResponsiveMasonry> */}
        </Box>
        {isLoader && <Loader />}
      </Box>

      <Box
        fontFamily="FedraSansStd-Medium"
        fontSize="18px"
        lineHeight="1.25"
        color="#cf4824"
        textDecoration="underline"
        mb="20px"
        textAlign="center"
      >
        <NextLink
          href={`/${language}/wisdom?topic=${pageData.urlAlias}`}
          prefetch={false}
          passHref
          legacyBehavior
        >
          <Link>
            <Box mb="20px" cursor="pointer">
              Read more Sadhguru&apos;s Wisdom on {pageData.title}
            </Box>
          </Link>
        </NextLink>
        <Box>&nbsp;</Box>
        <NextLink
          href={`/${language}/blog?topic=${pageData.urlAlias}`}
          prefetch={false}
          legacyBehavior
        >
          <Link>
            <Box mb="25px" cursor="pointer">
              Read more articles from Isha on {pageData.title}
            </Box>
          </Link>
        </NextLink>
      </Box>
    </Box>
  ) : (
    ''
  );
}

export const getStaticPaths = async () => {
  const wisdomLandingPaths = config.USE_LOCAL_URL
    ? [
      `/in/topic/sadhanapada-participants-interviews-ac`,
      '/en/topic/ignorance',
      '/te/topic/sadagaurau-laeekha',
    ]
    : await getWisdomLandingPaths('L1', '/topic/', '/news-category');
  return {
    paths: wisdomLandingPaths,
    fallback: false,
  };
};

export const getStaticProps = async ({
  preview,
  previewData,
  params: { region, topic },
  req,
}) => {
  const language = region;
  const headerTopMenuData = await fetchHeaderTopMenuData(
    getLocale('', language),
    'isotopmenu'
  );
  const headerMenuData = await fetchHeaderMenuData(
    getLocale('', language),
    'iso_non_regional_menu'
  );
  const footerData = await fetchFooterData(
    getLocale('', language),
    'isofooter'
  );
  const pageData = await fetchTopicData('', language, topic, {
    preview,
    data: previewData,
  });
  // console.log('pageData', pageData);

  return {
    props: {
      headerTopMenuData: headerTopMenuData?.data || null,
      headerMenuData: headerMenuData?.data || null,
      footerData: footerData?.data || null,
      region: '',
      language: language,
      pageData: pageData || null,
      page: topic,
      isPreview: preview ? true : false,
    },
  };
};
